import { useMemo } from "react";
import { Box, Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { FoodsTabs } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";
import { CONSENT_GOVERNANCE_FEATURE_SWITCH } from "src/api/client-api/consent/constants";
import { URLHandler } from "src/services/URLHandler";
import { useSnackbar } from "src/utils/useSnackbar";
import { ConsentCreationDrawer } from "./ConsentCreationDrawer";
import { ConsentItemModal } from "./ConsentItemModal";
import { ConsentItemVersionModal } from "./ConsentItemVersionModal";
import { ConsentPreviewDialog } from "./ConsentPreviewDialog";
import { TabLabel } from "./components/ConsentAdminShared";
import { ConsentGovernanceDisabledState } from "./components/ConsentGovernanceDisabledState";
import { ConsentGovernanceHeader } from "./components/ConsentGovernanceHeader";
import { ConsentGovernanceSummaryCards } from "./components/ConsentGovernanceSummaryCards";
import { ConsentItemsTab } from "./components/ConsentItemsTab";
import { ConsentRecordsTab } from "./components/ConsentRecordsTab";
import { ConsentVersionsTab } from "./components/ConsentVersionsTab";
import { ConsentDeleteDialog } from "./dialogs/ConsentDeleteDialog";
import {
    CONSENT_ADMIN_TAB_IDS,
    CONSENT_ADMIN_TAB_PARAM,
    DOCUMENTS_TAB,
    OPT_INS_TAB,
    RECORDS_TAB,
    VERSIONS_TAB,
    type ConsentAdminTabId,
} from "./consentGovernanceTypes";
import { useConsentGovernanceActions } from "./hooks/useConsentGovernanceActions";
import { useConsentGovernanceData } from "./hooks/useConsentGovernanceData";
import { useConsentGovernanceDialogs } from "./hooks/useConsentGovernanceDialogs";
import { useConsentGovernanceLocales } from "./hooks/useConsentGovernanceLocales";
import { ROWS_PER_PAGE_OPTIONS, useConsentGovernanceTabState } from "./hooks/useConsentGovernanceTabState";

const getConsentAdminTabId = (tabId: string | null): ConsentAdminTabId =>
    CONSENT_ADMIN_TAB_IDS.includes(tabId as ConsentAdminTabId) ? (tabId as ConsentAdminTabId) : DOCUMENTS_TAB;

export const ConsentGovernanceSettings = () => {
    const theme = useTheme();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const snackbar = useSnackbar();
    const [searchParams, setSearchParams] = useSearchParams();
    const { isFeatureActivated } = useApplicationParameters();
    const isConsentGovernanceEnabled = isFeatureActivated(CONSENT_GOVERNANCE_FEATURE_SWITCH);
    const locales = useConsentGovernanceLocales(i18n);
    const data = useConsentGovernanceData({
        isConsentGovernanceEnabled,
        selectedLocale: locales.selectedLocale,
        uiLocale: locales.uiLocale,
    });
    const tabState = useConsentGovernanceTabState({
        consentItemVersions: data.consentItemVersions,
        documents: data.documents,
        latestConsentItemVersions: data.latestConsentItemVersions,
        optIns: data.optIns,
        selectedLocale: locales.selectedLocale,
    });
    const selectedTabId = getConsentAdminTabId(searchParams.get(CONSENT_ADMIN_TAB_PARAM));

    const tabs = useMemo(
        () => [
            {
                id: DOCUMENTS_TAB,
                name: (
                    <TabLabel label={t("settings.consent.tabs.documents", "Documents")} count={data.totalDocuments} />
                ),
            },
            {
                id: OPT_INS_TAB,
                name: (
                    <TabLabel label={t("settings.consent.tabs.preferences", "Preferences")} count={data.totalOptIns} />
                ),
            },
            {
                id: VERSIONS_TAB,
                name: (
                    <TabLabel
                        label={t("settings.consent.tabs.versions", "Versions")}
                        count={data.totalConsentItemVersions}
                    />
                ),
            },
            {
                id: RECORDS_TAB,
                name: (
                    <TabLabel label={t("settings.consent.tabs.records", "Records")} count={data.totalConsentRecords} />
                ),
            },
        ],
        [data.totalDocuments, data.totalOptIns, data.totalConsentItemVersions, data.totalConsentRecords, t],
    );

    const selectedTabIndex = Math.max(
        tabs.findIndex((tab) => tab.id === selectedTabId),
        0,
    );

    const switchToTab = (tabId: ConsentAdminTabId) => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set(CONSENT_ADMIN_TAB_PARAM, tabId);
        tabState.setSelectedItemIds(new Set());
        tabState.setSelectedVersionIds(new Set());
        setSearchParams(nextParams, { replace: true });
    };

    const dialogs = useConsentGovernanceDialogs({
        consentItems: data.consentItems,
        setSelectedLocale: locales.setSelectedLocale,
        switchToTab,
    });
    const actions = useConsentGovernanceActions({
        closeDeleteDialog: dialogs.closeDeleteDialog,
        deleteTarget: dialogs.deleteDialogState.target,
        setSelectedItemIds: tabState.setSelectedItemIds,
        setSelectedVersionIds: tabState.setSelectedVersionIds,
        snackbar,
        t,
    });

    return (
        <Box sx={{ p: theme.spacing(6), mx: "auto" }}>
            <Stack gap={theme.spacing(4)}>
                <ConsentGovernanceHeader
                    availableContentLocales={locales.availableContentLocales}
                    isLocalesLoading={locales.isLocalesLoading}
                    selectedLocale={locales.selectedLocale}
                    selectedLocaleMeta={locales.selectedLocaleMeta}
                    onLocaleChange={locales.setSelectedLocale}
                />

                {isConsentGovernanceEnabled ?
                    <>
                        <ConsentGovernanceSummaryCards
                            documentCount={data.totalDocuments}
                            optInCount={data.totalOptIns}
                            recordCount={data.totalConsentRecords}
                            versionCount={data.totalConsentItemVersions}
                        />

                        <FoodsTabs
                            tabs={tabs.map((tab) => ({
                                id: tab.id,
                                name: tab.name,
                                TabProps: { onClick: () => switchToTab(tab.id) },
                            }))}
                            tabVariant="buttons"
                            selectedTabIndex={selectedTabIndex}
                            forceScrollable
                        />

                        {selectedTabId === DOCUMENTS_TAB && (
                            <ConsentItemsTab
                                allVisibleSelected={tabState.areAllVisibleDocumentsSelected}
                                bulkActionLoading={actions.bulkActionLoading}
                                highlightedItemId={dialogs.highlightedCreatedItemId}
                                isLoading={data.isDocumentsLoading}
                                isPatchPending={actions.isPatchPending}
                                items={data.documents}
                                kind="documents"
                                latestVersionByItemId={tabState.latestVersionByItemId}
                                page={data.documentsPage}
                                paginatedItems={tabState.paginatedDocuments}
                                rowsPerPage={data.documentsRowsPerPage}
                                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                                selectedItemIds={tabState.selectedItemIds}
                                selectedItems={tabState.selectedDocuments}
                                totalItems={data.totalDocuments}
                                onAdd={() => dialogs.openCreateConsentDrawer("document")}
                                onBulkDelete={dialogs.openBulkDeleteItemsDialog}
                                onDelete={dialogs.openDeleteItemDialog}
                                onEdit={dialogs.openEditItemModal}
                                onPageChange={data.setDocumentsPage}
                                onPreview={dialogs.openPreviewDialog}
                                onRowsPerPageChange={(rowsPerPage) => {
                                    data.handleDocumentsRowsPerPageChange(rowsPerPage);
                                    tabState.setSelectedItemIds(new Set());
                                }}
                                onSelect={tabState.toggleSelectedItem}
                                onSetVisibleSelection={tabState.setVisibleItemSelection}
                                onToggleActive={actions.handleToggleItemActive}
                            />
                        )}

                        {selectedTabId === OPT_INS_TAB && (
                            <ConsentItemsTab
                                allVisibleSelected={tabState.areAllVisibleOptInsSelected}
                                bulkActionLoading={actions.bulkActionLoading}
                                highlightedItemId={dialogs.highlightedCreatedItemId}
                                isLoading={data.isOptInsLoading}
                                isPatchPending={actions.isPatchPending}
                                items={data.optIns}
                                kind="optIns"
                                latestVersionByItemId={tabState.latestVersionByItemId}
                                page={data.optInsPage}
                                paginatedItems={tabState.paginatedOptIns}
                                rowsPerPage={data.optInsRowsPerPage}
                                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                                selectedItemIds={tabState.selectedItemIds}
                                selectedItems={tabState.selectedOptIns}
                                totalItems={data.totalOptIns}
                                onAdd={() => dialogs.openCreateConsentDrawer("opt_in")}
                                onBulkDelete={dialogs.openBulkDeleteItemsDialog}
                                onBulkSetActive={(items, active) => actions.handleBulkSetOptInsActive(items, active)}
                                onDelete={dialogs.openDeleteItemDialog}
                                onEdit={dialogs.openEditItemModal}
                                onPageChange={data.setOptInsPage}
                                onPreview={dialogs.openPreviewDialog}
                                onRowsPerPageChange={(rowsPerPage) => {
                                    data.handleOptInsRowsPerPageChange(rowsPerPage);
                                    tabState.setSelectedItemIds(new Set());
                                }}
                                onSelect={tabState.toggleSelectedItem}
                                onSetVisibleSelection={tabState.setVisibleItemSelection}
                                onToggleActive={actions.handleToggleItemActive}
                            />
                        )}

                        {selectedTabId === VERSIONS_TAB && (
                            <ConsentVersionsTab
                                allVisibleSelected={tabState.areAllVisibleVersionsSelected}
                                bulkActionLoading={actions.bulkActionLoading}
                                consentItems={data.consentItems}
                                filteredVersions={tabState.filteredVersions}
                                isLoading={data.isConsentItemVersionsLoading}
                                page={data.versionsPage}
                                paginatedVersions={tabState.paginatedVersions}
                                rowsPerPage={data.versionsRowsPerPage}
                                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                                selectedVersionIds={tabState.selectedVersionIds}
                                selectedVersions={tabState.selectedVersions}
                                totalVersions={data.totalConsentItemVersions}
                                uiLocale={locales.uiLocale}
                                versionPurposeFilter={data.versionPurposeFilter}
                                onAddVersion={() => dialogs.openCreateVersionModal()}
                                onBulkDeleteVersions={dialogs.openBulkDeleteVersionsDialog}
                                onDeleteVersion={dialogs.openDeleteVersionDialog}
                                onEditVersion={dialogs.openEditVersionModal}
                                onPageChange={data.setVersionsPage}
                                onPreviewVersion={dialogs.openVersionPreviewDialog}
                                onPurposeFilterChange={(itemId) => {
                                    data.setVersionPurposeFilter(itemId);
                                    tabState.setSelectedVersionIds(new Set());
                                }}
                                onRowsPerPageChange={(rowsPerPage) => {
                                    data.handleVersionsRowsPerPageChange(rowsPerPage);
                                    tabState.setSelectedVersionIds(new Set());
                                }}
                                onSetVisibleVersionSelection={tabState.setVisibleVersionSelection}
                                onToggleVersionSelection={tabState.toggleSelectedVersion}
                            />
                        )}

                        {selectedTabId === RECORDS_TAB && (
                            <ConsentRecordsTab
                                consentRecords={data.consentRecords}
                                decisionFilter={data.recordDecisionFilter}
                                filteredRecords={data.filteredRecords}
                                isLoading={data.isConsentRecordsLoading}
                                page={data.recordsPage}
                                paginatedRecords={data.paginatedRecords}
                                recordSearch={data.recordSearch}
                                rowsPerPage={data.recordsRowsPerPage}
                                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                                sourceFilter={data.recordSourceFilter}
                                totalRecords={data.recordPaginationTotal}
                                typeFilter={data.recordTypeFilter}
                                uiLocale={locales.uiLocale}
                                onDecisionFilterChange={data.setRecordDecisionFilter}
                                onPageChange={data.setRecordsPage}
                                onRecordSearchChange={data.setRecordSearch}
                                onRowsPerPageChange={data.handleRecordsRowsPerPageChange}
                                onSourceFilterChange={data.setRecordSourceFilter}
                                onTypeFilterChange={data.setRecordTypeFilter}
                            />
                        )}
                    </>
                :   <ConsentGovernanceDisabledState
                        onOpenFeatureSwitches={() => navigate(URLHandler.settings.features())}
                    />
                }
            </Stack>
            <ConsentItemModal
                open={dialogs.itemModalState.open}
                itemType={dialogs.itemModalState.itemType}
                consentItem={dialogs.itemModalState.consentItem}
                locale={locales.selectedLocale}
                localeOptions={locales.availableContentLocales}
                onClose={dialogs.closeItemModal}
                onSuccess={dialogs.handleItemModalSuccess}
            />
            <ConsentItemVersionModal
                open={dialogs.versionModalState.open}
                consentItems={data.consentItems}
                consentItemType={dialogs.versionModalState.consentItemType}
                consentVersion={dialogs.versionModalState.consentVersion}
                locale={locales.selectedLocale}
                localeOptions={locales.availableContentLocales}
                preselectedConsentItem={dialogs.versionModalState.preselectedConsentItem}
                onClose={dialogs.closeVersionModal}
            />
            <ConsentCreationDrawer
                open={dialogs.creationDrawerState.open}
                initialType={dialogs.creationDrawerState.initialType}
                initialLocale={locales.selectedLocale}
                localeOptions={locales.availableContentLocales}
                onClose={dialogs.closeCreationDrawer}
                onCreated={dialogs.handleConsentCreationDrawerCreated}
            />
            <ConsentPreviewDialog
                open={dialogs.previewDialogState.open}
                item={dialogs.previewDialogState.item}
                locale={locales.selectedLocale}
                localeOptions={locales.availableContentLocales}
                version={dialogs.previewDialogState.version}
                onClose={dialogs.closePreviewDialog}
            />
            <ConsentDeleteDialog
                open={dialogs.deleteDialogState.open}
                target={dialogs.deleteDialogState.target}
                isDeleting={actions.isDeleting}
                onClose={dialogs.closeDeleteDialog}
                onConfirm={() => void actions.handleDeleteConfirm()}
            />
        </Box>
    );
};
